import { CheckOutlined } from "@ant-design/icons";
import { Grid } from "@mui/material";
import { styled } from "@mui/material/styles";
import moment from "moment";
import React, { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { WhoAmI } from "../../@types/UserInfo";
import { QueryByStringWithClient } from "../../api/apollo";
import { graphqlStringMap } from "../../api/fetchByTypes";
import AppBreadcrumb from "../../components/AppBreadcrumb";
import CustomLoading from "../../components/CustomLoading/CustomLoading";
import UserContext from "../../provider/UserContextProvider";
import { formatUserName } from "../../utils/getDataUtil";
import { PreCountryDataResponseType } from "../PreCountryPage/type";
import DesignModule from "./components/DesignModule";
import IndicationsCard, {
  IndicationsCardProps,
} from "./components/DesignModule/IndicationsCard";
import { useDeleteIndication } from "./useDeleteIndication";
import { checkIfStringIsUnixTimestamp } from "../../components/PageTag";

const greeting = "What would you like to do today?";
const updateFormat = "MMM D, YYYY hh:mm A";

export interface ColDataProps {
  key: string;
  value: number;
  label: string;
}

interface SiteSelection {
  status: string;
  siteSelectionId: string;
  sitePursued: number;
  siteContacted: number;
  sipSelected: number;
  selectedIndication: string;
  sitesNotStarted: number;
  siteIncluded: number;
  therapeuticArea: string;
  createdAt: string;
  lastUpdatedBy: string;
  updatedAt: string;
}
interface TrialProps {
  studyAlias?: string;
  updatedAt?: string;
  lastUpdatedBy?: string;
  status?: string;
  createdAt?: string;
}
interface CountryInfoProps {
  status: string;
  opt: string;
  totalPatients: number;
  totalSites: number;
  total: number;
  completed: number;
}
interface PreCountryValidation {
  id: string;
  studyId: string;
  studyAlias: string;
  status: string;
  locked: boolean;
  lockedBy: string;
  lastUpdatedBy: string;
  question: string;
  createdAt: string;
  updatedAt: string;
}
interface SiteInfoProps {
  status: string;
  totalSites: number;
  sitePursued: number;
}

const initCountryInfo: CountryInfoProps = {
  status: "",
  opt: "",
  totalPatients: 0,
  totalSites: 0,
  total: 0,
  completed: 0,
};
const initPreCountryInfo: PreCountryValidation = {
  id: "",
  studyId: "",
  studyAlias: "",
  status: "",
  locked: false,
  lockedBy: "",
  lastUpdatedBy: "",
  question: "",
  createdAt: "",
  updatedAt: "",
};

const initSiteInfo: SiteInfoProps = {
  status: "",
  totalSites: 0,
  sitePursued: 0,
};

const LandingPage: React.FC<Record<string, never>> = () => {
  const { studyAlias, ta }: any = useParams();
  const userInfo: WhoAmI | undefined = useContext(UserContext);
  const [loading, setLoading] = useState(true);
  const { deleteIndicationMutation } = useDeleteIndication();
  const [trial, setTrial] = useState<TrialProps>({});
  const [countryInfo, setCountryInfo] =
    useState<CountryInfoProps>(initCountryInfo);
  const [preCountryInfo, setPreCountryInfo] =
    useState<PreCountryValidation>(initPreCountryInfo);
  const [siteInfo, setSiteInfo] = useState<SiteInfoProps>(initSiteInfo);
  const [countryCols, setCountryCols] = useState<ColDataProps[]>([]);
  const [preCountryCols, setPreCountryCols] = useState<ColDataProps[]>([]);
  const [siteCols, setSiteCols] = useState<ColDataProps[]>([]);
  const [indicationsMetadata, setIndicationsMetadata] =
    useState<IndicationsCardProps["metadata"]>();
  const [indicationsData, setIndicationsData] =
    useState<IndicationsCardProps["indications"]>();

  const getCountryAndSiteInfo = () => {
    setLoading(true);
    QueryByStringWithClient(graphqlStringMap.fetchCountryAndSiteInfo, {
      studyAlias,
    })
      .then((res) => {
        const country = res.data.countryVerificationTotals;
        const sites: SiteSelection[] = res.data.siteSelectionsSearch.pislData;
        const preCountry: PreCountryValidation | undefined =
          res.data.preCountryValidation;
        if (country) {
          setCountryInfo({
            status: country.status,
            opt: country.opt,
            totalPatients: country.totalPatients,
            totalSites: country.totalSites,
            total: country.totalCountries,
            completed: country.completedCountries,
          });
        } else {
          setCountryInfo(initCountryInfo);
        }
        if (preCountry) {
          setPreCountryInfo(preCountry);
        } else {
          setPreCountryInfo(initPreCountryInfo);
        }
        if (sites.length > 0) {
          let sitesPursued = 0;
          let availableSites = 0;
          let sitesNotStarted = 0;
          let sitesContacted = 0;
          const indications: IndicationsCardProps["indications"] = [];
          sites.forEach((site: SiteSelection) => {
            sitesPursued += site.sitePursued;
            availableSites += site.siteIncluded;
            sitesNotStarted += site.sitesNotStarted;
            sitesContacted += site.siteContacted;
            indications.push({
              key: site.selectedIndication,
              therapeuticArea: site.therapeuticArea,
              value: site.siteIncluded,
              label: `# OF SITES AVAILABLE ON ${site.selectedIndication} PISL`,
            });
          });
          const metadata: ColDataProps[] = [
            {
              key: "notStarted",
              label: "# OF SITES NOT STARTED IN PISL",
              value: sitesNotStarted,
            },
            {
              key: "pursued",
              label: "# OF SITES PURSUED IN PISL",
              value: sitesPursued,
            },
            {
              key: "contacted",
              label: "# OF SITES CONTACTED IN PISL",
              value: sitesContacted,
            },
          ];
          setIndicationsData(indications);
          setIndicationsMetadata(metadata);
          setSiteInfo({
            status: sites[0].status,
            totalSites: availableSites,
            sitePursued: sitesPursued,
          });
        } else {
          setIndicationsData(undefined);
          setIndicationsMetadata(undefined);
          setSiteInfo(initSiteInfo);
        }
        if (!preCountry && !country && sites.length === 0) {
          setTrial({});
          return;
        }
        setTrial({
          studyAlias: studyAlias,
          updatedAt:
            preCountry?.updatedAt || country?.updatedAt || sites[0]?.updatedAt,
          createdAt:
            preCountry?.createdAt || country?.createdAt || sites[0]?.createdAt,
          lastUpdatedBy:
            preCountry?.lastUpdatedBy ||
            country?.lastUpdatedBy ||
            sites[0]?.lastUpdatedBy,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const deleteIndication = (indication: string, ta: string) => {
    setLoading(true);
    deleteIndicationMutation({
      studyAlias,
      selectedIndication: indication,
      therapeuticArea: ta,
    })
      .then(() => {
        getCountryAndSiteInfo();
      })
      .catch((error: any) => {
        console.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const getPreCountryDatas = () => {
    QueryByStringWithClient<PreCountryDataResponseType>(
      graphqlStringMap.fethPreCountryData,
      {
        studyAlias,
      }
    ).then((res: { data: PreCountryDataResponseType }) => {
      const preCountries = res.data.preCountryDatas;
      if (preCountries) {
        const cols: Array<ColDataProps> = [
          {
            key: "total",
            value: preCountries.length,
            label: "TOTAL COUNTRIES INCLUDED IN VALIDATION",
          },
        ];
        setPreCountryCols(cols);
      }
    });
  };
  useEffect(() => {
    getCountryAndSiteInfo();
    getPreCountryDatas();
  }, []);

  useEffect(() => {
    const cols: Array<ColDataProps> = [
      {
        key: "totalPatients",
        value: countryInfo.totalPatients,
        label: "TOTAL PATIENTS INCLUDED IN VALIDATION",
      },
      {
        key: "totalSites",
        value: countryInfo.totalSites,
        label: "TOTAL SITES INCLUDED IN VALIDATION",
      },
      {
        key: "total",
        value: countryInfo.total,
        label: "TOTAL COUNTRIES INCLUDED IN VALIDATION",
      },
    ];
    setCountryCols(cols);
  }, [countryInfo]);

  useEffect(() => {
    const cols: Array<ColDataProps> = [
      {
        key: "totalSites",
        value: siteInfo.totalSites,
        label: "# OF SITES AVAILABLE ON PISL",
      },
      {
        key: "sitesPursued",
        value: siteInfo.sitePursued,
        label: "# OF SITES PURSUED",
      },
    ];
    setSiteCols(cols);
  }, [siteInfo]);

  return (
    <PageContainer>
      <CustomLoading open={loading} />
      <BreadcrumbContainer>
        <AppBreadcrumb
          links={[
            { href: "/", text: "" },
            { href: "/all-trials", text: "ALL TRIALS" },
            {
              href: `/trial-details/${studyAlias}`,
              text: "TRIAL DETAILS",
            },
          ]}
        />
      </BreadcrumbContainer>
      <Upper>
        <div className="trial-alias">{trial?.studyAlias}</div>
        {trial.createdAt && (
          <span>
            <span className="author">
              Created At{" "}
              {moment(
                trial.createdAt
                  ? checkIfStringIsUnixTimestamp(trial.createdAt)
                    ? moment(Number(trial.createdAt)).format(updateFormat)
                    : moment(trial.createdAt).format(updateFormat)
                  : null
              ).format(updateFormat)}
            </span>
          </span>
        )}
        {trial.updatedAt && trial.lastUpdatedBy && (
          <span>
            <span className="author">
              <CheckOutlined /> Last Saved{" "}
              {moment(
                trial.updatedAt
                  ? checkIfStringIsUnixTimestamp(trial.updatedAt)
                    ? moment(Number(trial.updatedAt)).format(updateFormat)
                    : moment(trial.updatedAt).format(updateFormat)
                  : null
              ).format(updateFormat)}{" "}
              by {formatUserName(trial.lastUpdatedBy)}
            </span>
          </span>
        )}
      </Upper>
      <Greeting>{greeting}</Greeting>
      <ModuleContainer>
        <DesignModule
          type="pre-country"
          title="Preliminary Country List"
          status={preCountryInfo?.status}
          datasource={preCountryCols}
          opt=""
          viewLink={`/pre-country-verification/${studyAlias}`}
          viewButtonText="View Preliminary Country List"
          total={preCountryCols[0]?.value || 0}
          disableProgress
        />
        <DesignModule
          type="country"
          title="Country Validation"
          status={countryInfo.status}
          datasource={countryCols}
          opt={countryInfo.opt}
          viewLink={`/country-verification/${studyAlias}`}
          viewButtonText="View Country List"
          total={countryInfo.total}
          progress={countryInfo.completed}
        />
        <DesignModule
          type="site"
          title="Potential Investigator / Site List"
          status={siteInfo?.status}
          datasource={siteCols}
          viewLink={`/site-selection/${studyAlias}/${ta}`}
          viewButtonText="View Potential Investigator / Site List"
          total={siteInfo.totalSites}
          progress={siteInfo.sitePursued}
        />
        {indicationsData && indicationsMetadata && userInfo && (
          <IndicationsCard
            metadata={indicationsMetadata}
            indications={indicationsData}
            handleDelete={deleteIndication}
            isInformaticist={userInfo.whoami.isInformaticist}
          />
        )}
      </ModuleContainer>
    </PageContainer>
  );
};
export const PageContainer = styled("div")({
  padding: "0 7rem",
});
export const BreadcrumbContainer = styled(Grid)({
  marginBottom: "2.125rem",
  marginTop: "3.25rem",
});
export const Upper = styled(Grid)({
  "& .due-date": {
    color: "#252525",
    fontSize: "0.68rem",
  },
  "& .trial-alias": {
    color: "#000000",
    fontSize: "1.25rem",
  },
  "& .author": {
    display: "block",
    color: "#A59D95",
    fontSize: "0.75rem",
  },
});
const Greeting = styled("span")({
  color: "#252525",
  fontSize: "1.5rem",
  display: "flex",
  margin: "4.25rem 0 3.15rem 0",
});
const ModuleContainer = styled("div")({
  paddingBottom: "7.375rem",
});

export default LandingPage;
